import type { ListElementValue, ListItem } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageSource, FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { generateId } from '@vedalib/editor/lib/utils/id'
import cn from 'classnames'
import * as R from 'ramda'
import { useMemo } from 'react'
import { useCallback } from 'react'
import React from 'react'
import { FileTypeEnum } from 'utils'

import FloatingPanel from 'components/Editor/FloatingPanel'
import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import { chainName, FormField, FieldFile, FieldNumber } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import type { IFieldConfig } from 'components/formKit/types'
import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton, TileButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'
import { Typography } from 'components/uiKit/Typography'
import { useElementStyleValues } from 'services/Branding/hooks'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getDeviceMode } from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { getFileByUsage, getOriginalImageById } from 'utils/files'

import * as s from './ListFormValueItem.module.scss'
import type { ListElementEditorType } from './listTypes'

const useDefaultIcon = ({
  name,
}: {
  name: string
}): Partial<IFieldConfig<Block, React.ComponentProps<typeof FieldFile<FileUsage | null>>>> => {
  const device = useProjectContext(getDeviceMode)
  const { style } = useFormData<ListElementEditorType>(name, 4) || {}
  const elementStyles = useElementStyleValues('list', device, style)
  const defaultValue = useMemo(() => {
    if (!elementStyles?.labels.backgroundImage) {
      return null
    }
    return {
      id: elementStyles?.labels.backgroundImage,
      type: FileUsageTypeEnum.image,
      source: FileUsageSource.file,
    } satisfies FileUsage
  }, [elementStyles?.labels.backgroundImage])

  return useMemo(() => ({ defaultValue }), [defaultValue])
}

const NUMBER = genElementValueField({
  name: 'number',
  as: FieldNumber,
  rules: [
    { min: 0, max: 99, message: t('input.error.numberRange', { from: 0, to: 99 }), required: true },
    {
      validator: (_rule, value) => {
        if (value === null || value === undefined) {
          return t('input.error.numberRange', { from: 0, to: 99 })
        }
        return false
      },
    },
  ],
})

const ICON = genElementValueField({
  name: 'icon',
  as: FieldFile,
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
    preview: true,
  },
  useConfigs: [useDefaultIcon],
})

export const ListFormValueItem: React.FC<IRenderItemProps<ListItem>> = ({
  index,
  name,
  drag,
  onChange,
  value: array,
  item,
  min,
  max,
}) => {
  const value = useFormData<ListElementValue>(name, 1)
  const brandIcon = useDefaultIcon({ name: `${name}.${index}. ${ICON.name}` })
  const defaultIcon = getOriginalImageById(
    !isMixed(brandIcon.defaultValue) ? brandIcon.defaultValue?.id : undefined,
  )
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)
  const file = getFileByUsage(item.icon, FileUsageTypeEnum.image)
  const onClone = useCallback(
    () =>
      onChange?.([...array, { ...R.clone(array[index]), value: generateId() }], {
        command: 'clone',
        item: array[index],
      }),
    [onChange, array, index],
  )

  const onDelete = useCallback(
    () =>
      onChange?.(
        array.filter((_item, i) => i !== index),
        { command: 'delete', item: array[index] },
      ),
    [onChange, array, index],
  )

  return (
    <div className={s.root}>
      <div className={cn(s.header, { [s.point]: value.mode === 'point' })} ref={drag}>
        <span className={s.title}>
          <Icon name='builderDrugAndDrop' size={KitSize.S} />
          <Typography>
            {t('elements.accordion.form.component')} {index + 1}
          </Typography>
        </span>
        <span className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
            <IconButton
              disabled={disableClone}
              icon='otherCopy'
              name='clone'
              onClick={onClone}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name='delete'
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </span>
      </div>

      {value.mode === 'images' && (
        <FloatingPanel
          header={<Typography weight='medium'>{t('page.finder.tag.image')}</Typography>}
          panel={
            <Padding gap>
              <FormField config={chainName(name, `${index}`, ICON)} />
            </Padding>
          }
        >
          {({ open }) => (
            <TileButton active={open} icon='otherAdd' name='settings' styleType='ghost' fluid>
              <PanelIconImage defaultPath={defaultIcon?.path} file={item.icon} />
              <Typography rows={1} styleType='hint' tooltip={file?.name}>
                {file?.name || t('page.finder.tag.image')}
              </Typography>
            </TileButton>
          )}
        </FloatingPanel>
      )}
      {value.mode === 'number' && (
        <Padding bottom gap sides top>
          <FormField config={chainName(name, `${index}`, NUMBER)} />
        </Padding>
      )}
    </div>
  )
}

export default React.memo(ListFormValueItem)
